import React, { useState, useEffect } from 'react';
import '../../../styles/components/GptInsights.scss';

interface Props {
  storeId: string;
}

const GptInsightsModal: React.FC<Props> = ({ storeId }) => {
  const [question, setQuestion] = useState('');
  const [gptAnswer, setGptAnswer] = useState<string | null>(null);
  const [isAsking, setIsAsking] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const askGPT = async () => {
    if (!storeId) {
      setGptAnswer("❌ Error: No store selected.");
      return;
    }

    setIsAsking(true);
    setGptAnswer(null);

    try {
      const response = await fetch('https://web-production-79b0f.up.railway.app/insights', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: question,
          store_id: storeId,
        }),
      });

      const result = await response.json();

      if (result?.response) {
        setGptAnswer(result.response);
      } else if (result?.error) {
        setGptAnswer(`❌ ${result.error}`);
        console.error("Insights error:", result.details || result.error);
      } else {
        setGptAnswer("❌ Unknown error. Try again later.");
      }
    } catch (err) {
      setGptAnswer("❌ Failed to fetch insights. Try again later.");
      console.error("GPT error:", err);
    }

    setIsAsking(false);
  };

  const handleSearchClick = () => {
    setIsOpen(true);
    // Small delay to allow the modal to open first
    setTimeout(() => setIsExpanded(true), 100);
  };

  const handleClose = () => {
    setIsExpanded(false);
    // Wait for collapse animation before closing modal
    setTimeout(() => {
      setIsOpen(false);
      setQuestion('');
      setGptAnswer(null);
    }, 300);
  };

  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, []);

  return (
    <>
      <div className="vivvy-search-container">
        <div className="vivvy-search-bar" onClick={handleSearchClick}>
          <div className="search-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9ZM19 9H14V4H5V21H19V9Z" fill="currentColor"/>
            </svg>
          </div>
          <div className="search-content">
            <span className="search-title">Vivvy AI Assistant</span>
            <span className="search-placeholder">Get insights about your business performance and customer feedback</span>
          </div>
          <div className="search-arrow">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="vivvy-modal-overlay">
          <div className={`vivvy-modal-content ${isExpanded ? 'expanded' : ''}`}>
            <div className="vivvy-modal-header">
              <div className="vivvy-avatar">
                <div className="vivvy-avatar-icon">V</div>
              </div>
              <div className="vivvy-header-text">
                <h3>Vivvy AI Assistant</h3>
                <span className="vivvy-status">Ready to help</span>
              </div>
              <button onClick={handleClose} className="vivvy-close-btn" aria-label="Close chat">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            <div className="vivvy-chat-container">
              <div className="vivvy-messages">
                <div className="vivvy-message vivvy-bot">
                  <div className="vivvy-avatar-small">V</div>
                  <div className="vivvy-message-content">
                    <p>Hi! I'm Vivvy, your AI business assistant. I can help you understand your customer feedback, identify trends, and provide insights about your business performance. What would you like to know?</p>
                  </div>
                </div>

                {question && (
                  <div className="vivvy-message vivvy-user">
                    <div className="vivvy-message-content">
                      <p>{question}</p>
                    </div>
                  </div>
                )}

                {isAsking && (
                  <div className="vivvy-message vivvy-bot">
                    <div className="vivvy-avatar-small">V</div>
                    <div className="vivvy-message-content">
                      <div className="vivvy-typing">
                        <div className="typing-dots">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                        <span className="typing-text">Vivvy is thinking...</span>
                      </div>
                    </div>
                  </div>
                )}

                {gptAnswer && (
                  <div className="vivvy-message vivvy-bot">
                    <div className="vivvy-avatar-small">V</div>
                    <div className="vivvy-message-content">
                      <p>{gptAnswer}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="vivvy-input-container">
              <div className="vivvy-input-wrapper">
                <textarea
                  placeholder="Ask me anything about your business..."
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  disabled={isAsking}
                  rows={1}
                  className="vivvy-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (question.trim() && !isAsking) {
                        askGPT();
                      }
                    }
                  }}
                />
                <button 
                  onClick={askGPT} 
                  disabled={isAsking || !question.trim()} 
                  className="vivvy-send-btn"
                  aria-label="Send message"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GptInsightsModal;
